import type { GetServerSideProps } from 'next'
import Link from 'next/link'
import dayjs from 'dayjs'
import { CtaBar } from '@/components/Seo/ContentBlocks'
import { fetchPublicMatches, PublicMatch } from '@/lib/serverApi'
import { matchTypeMap } from '@/utils/utils'
import { STATS } from '@/lib/seoContent'
import { absUrl, breadcrumbLd, sportOfMatchType, SeoProps } from '@/utils/seo'

const PAGE_SIZE = 100

// seo_index 的 match_time 取自 jsonb,是不带时区的 UTC 字符串(如 2026-10-22 10:34:27.552)
const matchDate = (m: PublicMatch) => dayjs(m.match_time ? `${m.match_time.replace(' ', 'T')}Z` : m.created_at)

type Props = { matches: PublicMatch[]; total: number; page: number }

// 赛事库:公开比赛的可爬索引页(/match 是带登录态的交互页,爬虫进不去),
// 每场比赛链到它的赛事详情页 /match/:id/info。
const Events = ({ matches, total, page }: Props) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const groups: { month: string; items: PublicMatch[] }[] = []
  matches.forEach((m) => {
    const month = matchDate(m).format('YYYY 年 M 月')
    const last = groups[groups.length - 1]
    if (last?.month === month) last.items.push(m)
    else groups.push({ month, items: [m] })
  })

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-10">
        <header className="flex flex-col gap-2">
          <h1 className="m-0 text-3xl font-black text-gray-900">赛事库{page > 1 ? ` · 第 ${page} 页` : ''}</h1>
          <p className="m-0 text-gray-500">
            全国各地使用灵缇赛事系统举办的公开比赛:掼蛋、团团转、复式赛、桥牌、匹克球……共 {total.toLocaleString()} 场公开赛事,平台累计承办 {STATS.matches} 场比赛。点进任一比赛可查看赛程对阵与成绩排名。
          </p>
        </header>
        {groups.map((g) => (
          <section key={g.month} className="flex flex-col gap-2">
            <h2 className="m-0 text-lg font-bold text-gray-800">{g.month}</h2>
            <ul className="m-0 flex list-none flex-col divide-y divide-gray-100 rounded-2xl bg-white p-0 ring-1 ring-black/5">
              {g.items.map((m) => (
                <li key={m.id} className="flex min-w-0 items-center gap-3 px-4 py-3">
                  <span className="w-12 shrink-0 text-xs text-gray-400">{matchDate(m).format('M/D')}</span>
                  <Link href={`/match/${m.id}/info`} className="min-w-0 flex-1 truncate text-gray-900 hover:text-[#DB3142]">
                    {m.name}
                  </Link>
                  <span className="hidden shrink-0 text-xs text-gray-400 sm:inline">
                    {sportOfMatchType(m.match_type)} · {matchTypeMap[m.match_type] || m.match_type}
                    {m.location ? ` · ${m.location}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <nav aria-label="分页" className="flex flex-wrap items-center justify-center gap-2 text-sm">
          {page > 1 && (
            <Link href={page === 2 ? '/events' : `/events?page=${page - 1}`} className="rounded-full bg-white px-3 py-1 text-gray-700 ring-1 ring-gray-200">
              ← 上一页
            </Link>
          )}
          <span className="text-gray-500">
            第 {page} / {pages} 页
          </span>
          {page < pages && (
            <Link href={`/events?page=${page + 1}`} className="rounded-full bg-white px-3 py-1 text-gray-700 ring-1 ring-gray-200">
              下一页 →
            </Link>
          )}
        </nav>
        <CtaBar />
      </div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query, res }) => {
  const page = Math.max(1, Number(query.page ?? 1) || 1)
  const { total, matches } = await fetchPublicMatches(page - 1, PAGE_SIZE)
  res.setHeader('Cache-Control', 'public, s-maxage=600, stale-while-revalidate=3600')
  const path = page > 1 ? `/events?page=${page}` : '/events'
  const seo: SeoProps = {
    title: `赛事库${page > 1 ? ` 第${page}页` : ''} · 全国掼蛋/复式赛/匹克球公开比赛与成绩 | 灵缇赛事系统`,
    description: `灵缇赛事库收录 ${total} 场使用灵缇赛事系统举办的公开比赛,覆盖掼蛋双人赛、团团转、复式赛、桥牌、匹克球等,可查看各场比赛的时间地点、赛程对阵与成绩排名。`,
    keywords: '掼蛋比赛,掼蛋比赛成绩,掼蛋赛事,掼蛋邀请赛,复式赛,匹克球比赛,赛事日历,比赛成绩查询',
    path,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '灵缇赛事库',
        url: absUrl(path),
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: total,
          itemListElement: matches.slice(0, 50).map((m, i) => ({ '@type': 'ListItem', position: (page - 1) * PAGE_SIZE + i + 1, url: absUrl(`/match/${m.id}/info`), name: m.name })),
        },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['赛事库', '/events'],
      ]),
    ],
  }
  return { props: { matches, total, page, seo } }
}

export default Events
